import { useEffect } from 'react';

/**
 * Per-page document metadata.
 *
 * Ported from the legacy application's `src/lib/usePageMeta.ts` (V2-018C-2
 * migration). Behaviour is unchanged: it sets the title, description, robots
 * directive, and canonical link on mount, restores the previous values on
 * unmount, and appends the OpenGraph and Twitter tags only when an `ogImage` is
 * supplied.
 *
 * Every value is applied after the script executes, which is the legacy
 * behaviour. Whether that is sufficient for the consumers the product cares
 * about is a separate question owned by the discoverability work; migrating it
 * unchanged is what parity requires.
 */
export interface PageMetaOptions {
  title: string;
  description?: string;
  noindex?: boolean;
  canonical?: string;
  ogImage?: string;
}

export function usePageMeta({
  title,
  description,
  noindex = false,
  canonical,
  ogImage,
}: PageMetaOptions): void {
  useEffect(() => {
    const prevTitle = document.title;
    const robotsEl = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    const prevRobots = robotsEl?.getAttribute('content') ?? null;
    const prevDesc = descEl?.getAttribute('content') ?? null;
    const prevCanonical = canonicalEl?.getAttribute('href') ?? null;

    document.title = title;

    if (description) {
      if (descEl) {
        descEl.setAttribute('content', description);
      } else {
        const el = document.createElement('meta');
        el.setAttribute('name', 'description');
        el.setAttribute('content', description);
        document.head.appendChild(el);
      }
    }

    const targetRobots = noindex ? 'noindex, nofollow' : 'index, follow';
    if (robotsEl) {
      robotsEl.setAttribute('content', targetRobots);
    } else {
      const el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      el.setAttribute('content', targetRobots);
      document.head.appendChild(el);
    }

    if (canonical) {
      if (canonicalEl) {
        canonicalEl.setAttribute('href', canonical);
      } else {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        el.setAttribute('href', canonical);
        document.head.appendChild(el);
      }
    }

    return () => {
      document.title = prevTitle;
      const r = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
      if (r && prevRobots !== null) r.setAttribute('content', prevRobots);
      const d = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (d && prevDesc !== null) d.setAttribute('content', prevDesc);
      const c = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (c && prevCanonical !== null) c.setAttribute('href', prevCanonical);
    };
  }, [title, description, noindex, canonical]);

  useEffect(() => {
    if (!ogImage) return;

    const defs: Array<[string, string, string]> = [
      ['property', 'og:type', 'website'],
      ['property', 'og:site_name', 'High Performer OS'],
      ['property', 'og:title', title],
      ['property', 'og:image', ogImage],
      ['name', 'twitter:card', 'summary_large_image'],
      ['name', 'twitter:title', title],
      ['name', 'twitter:image', ogImage],
    ];
    if (description) defs.push(['property', 'og:description', description]);
    if (canonical) defs.push(['property', 'og:url', canonical]);

    const els = defs.map(([attr, attrVal, content]) => {
      const el = document.createElement('meta');
      el.setAttribute(attr, attrVal);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      els.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [ogImage, title, description, canonical]);
}
